import { useCallback, useEffect, useState } from "react";
import { LoaderControl } from "../../../../components/LoaderControl";
import {
  getSummaryRangeDates,
  MDY_DATE_FORMAT_THREE,
} from "../../../../utils/filters";
import { Titlesubtitle } from "../../../../components/Common";
import { InputField } from "../../../../components/InputField";
import SearchIcon from "../../../../assets/icons/search-icon.png";
import ss from "../../../../components/InputField/input.module.scss";
import { TransactionTable } from "./components";
import { PaginationState } from "@tanstack/react-table";
import {
  SummaryRanges,
  WalletTransactionData,
} from "../../../../sharedTypes/wallet-types";
import { addLinkDownload, debounce } from "../../../../utils/index";
import { useToast } from "../../../../components/Toast/toast-context";
import { GenericFilter } from "../../../../components/Filters";
import { useGetHistory } from "../../../../hooks";
import { HistoryHeader } from "../../../../components/History";
import { useLazyGetDownloadHistoryQuery } from "../../../../requests/history";
import { txnStatusOptions } from "../../../../utils/data";
import { format, startOfYear, endOfYear, setDate } from "date-fns";

export function Transactions() {
  const [openFilterDialog, setOpenFilterDialog] = useState("");
  const [dateFilter] = useState<SummaryRanges>("THIS_YEAR");

  const [filter, setFilter] = useState<{
    from: string | Date;
    to: string | Date;
    status: string;
    search: string;
  }>({
    from: "",
    to: "",
    status: "",
    search: "",
  });

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 10,
  });

  const { startDate, endDate } = getSummaryRangeDates(dateFilter);

  const { getHistory, getCount, refetch, isLoading, isFetching, isError } =
    useGetHistory({
      name: "transactions",
      startDate: filter.from
        ? (filter.from as string)
        : format(startDate as any, MDY_DATE_FORMAT_THREE),
      endDate: filter.to
        ? (filter.to as string)
        : format(endDate as any, MDY_DATE_FORMAT_THREE),
      search: filter.search,
      transactionDirection: filter.status,
      page: pagination.pageIndex.toString(),
    });

  const [fetchCSV, { isFetching: csvFetching, isLoading: csvLoading }] =
    useLazyGetDownloadHistoryQuery();

  const toast = useToast();

  const debouncedSearch = useCallback(
    debounce((newSearch: string) => {
      setFilter((prevFilter) => ({ ...prevFilter, search: newSearch }));
    }, 1000),
    []
  );

  const closeFilterDialog = () => setOpenFilterDialog("");

  const handleRefetch = (
    to?: string | Date,
    from?: string | Date,
    status?: string
  ) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      to: to || "",
      from: from || "",
      status: status || "",
    }));
    closeFilterDialog();
  };

  let downloadCSV = async (
    to?: Date | string,
    from?: Date | string,
    status?: string
  ) => {
    const res = await fetchCSV({
      name: "transactions",
      fileType: "CSV",
      // startDate: from as string,
      // endDate: to as string,
      startDate:
        from || format(setDate(startOfYear(new Date()), 5), "yyyy-MM-dd"),
      endDate: to || format(endOfYear(new Date()), "yyyy-MM-dd"),
      transactionDirection: status,
    });

    if (res && res.isError) {
      toast?.open({
        variant: "warning",
        message: "An error occurred generating report",
      });
    } else {
      addLinkDownload(
        `${from?.toLocaleString()}_${to?.toLocaleString()}_transactions   `,
        res?.data?.data?.data as unknown as string
      );
      toast?.open({
        variant: "success",
        message: "Transactions was successfully downloaded",
      });
      closeFilterDialog();
    }
  };

  useEffect(() => {
    debouncedSearch(search);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, debouncedSearch]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [pagination]);

  return (
    <main className="reports-container tw-mx-auto md:tw-max-w-[80vw] lg:tw-max-w-[85vw] xl:tw-max-w-[90vw] 2xl:max-w-[100em] ">
      <div className="reports-container--wrapper tw-flex tw-flex-col tw-gap-[1.5em]">
        <HistoryHeader
          title="Transactions"
          openFilter={() => setOpenFilterDialog("filter")}
          downloadFn={() => setOpenFilterDialog("download")}
        />

        <div className="tw-w-full tw-flex tw-items-center tw-justify-between">
          <Titlesubtitle
            title="User wallet transactions"
            tclass="!tw-text-[1em] tw-text-neutral-900"
            subtitle="These are all the transactions that have occurred on your wallet"
            sclass="!tw-text-[.87em] tw-w-[80%]"
          />
          <div className="md:tw-w-[40%] 2xl:tw-w-[20%]">
            <InputField
              name="search"
              placeholder="Search "
              className="tw-w-full tw-h-[40px] !tw-bg-neutral-100 !tw-border-[5px] !tw-border-neutral-300"
              iconRight={
                <img
                  src={SearchIcon}
                  alt="search"
                  className={`${ss["iconriht"]} !tw-w-[20px] !tw-h-[20px] tw-absolute !tw-top-[25%] !tw-bottom-0 !tw-right-[5%]`}
                />
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearch(e.target.value);
              }}
            />
          </div>
        </div>

        <section className="tw-mt-[1.5em]">
          <LoaderControl
            loading={isLoading || isFetching}
            overlay={isLoading || isFetching}
            error={isError}
            errorControlOnClick={refetch}
            errorTitle="An error occurred. Please reload the widget."
          >
            {!isError && !isLoading && (
              <TransactionTable
                data={getHistory as WalletTransactionData[]}
                total={getCount as number}
                pagination={pagination}
                setPagination={setPagination}
              />
            )}
          </LoaderControl>
        </section>
      </div>

      {/* <ReportsFilter
        open={openFilterDialog}
        closeHandler={() => setOpenFilterDialog(false)}
        filterFn={handleRefetch}
      /> */}

      {/* for filter */}
      <GenericFilter
        title="Choose a period to filter transactions"
        btnText="Filter"
        open={openFilterDialog === "filter"}
        closeHandler={() => setOpenFilterDialog("")}
        filterFn={handleRefetch}
        downloadFn={downloadCSV}
        statusData={txnStatusOptions}
        statusText="Transaction Type"
        loading={isLoading || isFetching}
      />

      {/* for download */}
      <GenericFilter
        title="Choose a period to download transactions "
        btnText="Filter"
        open={openFilterDialog === "download"}
        statusData={txnStatusOptions}
        statusText="Transaction Type"
        closeHandler={closeFilterDialog}
        filterFn={downloadCSV}
        loading={csvFetching || csvLoading}
      />
    </main>
  );
}
